import { useEffect } from "react";
import {
  IconBrain,
  IconCircleCheck,
  IconPlugConnected,
  IconScale,
  IconTargetArrow,
  IconWorld,
} from "@tabler/icons-react";
import Footer from "../../components/Landing/components/Footer/Footer";
import Navbar from "../../components/Landing/components/Navbar/Navbar";
import { THE_TEAM, TEAM_MEMORIAL } from "../../constants";
import classes from "./team.module.css";

const DIFFERENTIATORS = [
  {
    icon: IconBrain,
    title: "Competency-based signals",
    body: "Situational judgement, logical reasoning and working-style evaluation replace guesswork with evidence of real ability.",
  },
  {
    icon: IconCircleCheck,
    title: "Verified candidates",
    body: "Candidate verification and data-backed profiles give employers more confidence in who they are actually hiring.",
  },
  {
    icon: IconTargetArrow,
    title: "Structured matching",
    body: "Consistent, role-relevant criteria and AI-assisted matching help connect the right people to the right roles.",
  },
  {
    icon: IconPlugConnected,
    title: "Assessments via API",
    body: "Our assessment engine is available through an API, so companies anywhere can screen and benchmark their own talent pools.",
  },
  {
    icon: IconWorld,
    title: "Nigeria-first, not Nigeria-only",
    body: "We launched with a Nigeria-first focus, but our tools are designed to be used well beyond it — locally, remotely and internationally.",
  },
  {
    icon: IconScale,
    title: "A fairer shot",
    body: "Structured, role-relevant evaluation lets talented professionals prove ability regardless of pedigree or network.",
  },
];

const COMPANY_INFO = [
  { k: "Company", v: "Awasource Ltd" },
  { k: "Founded", v: "2022" },
  { k: "Registered in", v: "Federal Republic of Nigeria" },
  { k: "Headquarters", v: "Abuja, Nigeria" },
];

const Team = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <div className={classes.page}>
      {/* HERO */}
      <section className={classes.hero}>
        <div className={classes.wrap}>
          <span className={classes.eyebrow}>About Awasource</span>
          <h1>Building a more trustworthy way to hire African talent</h1>
          <p className={classes.lede}>
            Awasource is the skills verification platform for hiring, built in Nigeria. We help companies find
            out who can do the job: businesses hire verified professionals across Africa with role-specific
            assessments, and partners anywhere build the same assessments into their own platforms through our
            API.
          </p>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className={classes.block}>
        <div className={`${classes.wrap} ${classes.twoCol}`}>
          <div>
            <span className={classes.kicker}>Who we are</span>
            <h2>A skills verification platform, built for evidence-based hiring</h2>
          </div>
          <div>
            <p className={classes.body}>
              Awasource Ltd is a Nigerian company founded in 2022, building the skills verification platform
              for hiring. We connect businesses with verified professionals across Africa, combining candidate
              verification, competency-based assessments and structured matching in one hiring marketplace.
            </p>
            <p className={classes.body}>
              Our goal is to help employers evaluate candidates using evidence of ability and role fit — not
              only CVs, credentials or personal networks. That same assessment capability is available via our
              API, so a company anywhere in the world can use Awasource to test its own candidates and existing
              talent pool, not just those hiring through our marketplace.
            </p>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className={`${classes.block} ${classes.alt}`}>
        <div className={`${classes.wrap} ${classes.twoCol}`}>
          <div>
            <span className={classes.kicker}>Why we built Awasource</span>
            <h2>Closing the gap between great talent and confident hiring</h2>
          </div>
          <div>
            <p className={classes.body}>
              African companies often struggle to identify qualified local candidates quickly and confidently.
              At the same time, international companies increasingly want to hire African professionals but
              may lack reliable ways to assess skills, working style and role fit from a distance.
            </p>
            <p className={classes.body}>
              Awasource was created to close that gap. Our platform serves Nigerian companies hiring locally
              and international companies seeking African talent — giving talented professionals a fairer
              opportunity to demonstrate what they can do, regardless of pedigree, background or professional
              network.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT MAKES US DIFFERENT */}
      <section className={classes.block}>
        <div className={classes.wrap}>
          <span className={classes.kicker}>What makes us different</span>
          <h2>Built around structured, competency-based assessment</h2>
          <p className={classes.body}>
            Our assessments may include situational judgement, logical reasoning, personality and
            working-style evaluation, and open-ended problem-solving. Candidates are evaluated using
            consistent, role-relevant criteria — giving employers more useful signals than self-reported
            credentials alone.
          </p>
          <div className={classes.cards}>
            {DIFFERENTIATORS.map(({ icon: Icon, title, body }) => (
              <div key={title} className={classes.card}>
                <div className={classes.cardIcon}>
                  <Icon size={22} />
                </div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI ERA */}
      <section className={`${classes.block} ${classes.alt}`}>
        <div className={`${classes.wrap} ${classes.twoCol}`}>
          <div>
            <span className={classes.kicker}>Hiring for the AI era</span>
            <h2>
              Measuring AI leverage <em>and</em> human judgement
            </h2>
          </div>
          <div>
            <p className={classes.body}>
              Modern work increasingly depends on how effectively people use artificial intelligence alongside
              professional judgement.
            </p>
            <p className={classes.body}>
              Awasource is developing role assessments that examine both AI leverage and human decision-making.
              This is intended to help employers understand not only whether candidates can use modern tools,
              but whether they can use them responsibly and effectively in real work. This is an active area of
              our product, and we are building it carefully.
            </p>
          </div>
        </div>
      </section>

      {/* DIRECTION */}
      <section className={classes.block}>
        <div className={`${classes.wrap} ${classes.twoCol}`}>
          <div>
            <span className={classes.kicker}>Our direction</span>
            <h2>Becoming trusted infrastructure for skills-based hiring</h2>
          </div>
          <div>
            <p className={classes.body}>
              Awasource launched with a Nigeria-first focus and is expanding its verified talent network across
              Africa, while opening its assessment tools to companies further afield through our API.
            </p>
            <p className={classes.body}>
              Our long-term goal is to become trusted infrastructure for companies hiring and evaluating
              talent — starting with African professionals, locally, remotely and internationally.
            </p>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className={`${classes.block} ${classes.alt}`}>
        <div className={classes.wrap}>
          <span className={classes.kicker}>Our team</span>
          <h2>The people who make Awasource tick</h2>
          <p className={classes.body}>
            A team on the path to launch Africa's biggest pride — a product worthy of worldwide use and
            recognition.
          </p>
          <div className={classes.teamGrid}>
            {THE_TEAM.map((member) => (
              <div key={member.name} className={classes.member}>
                <img className={classes.avatarImg} src={member.picture} alt={member.name} />
                <h3>{member.name}</h3>
                <div className={classes.role}>{member.role}</div>
                <p>{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IN MEMORY */}
      <section className={classes.block}>
        <div className={classes.wrap}>
          <span className={classes.kicker}>In memory</span>
          <h2>Remembering Stanley</h2>
          <div className={classes.memorial}>
            <img className={classes.memorialAvatar} src={TEAM_MEMORIAL.picture} alt={TEAM_MEMORIAL.name} />
            <div>
              <h3>{TEAM_MEMORIAL.name} 🕊️</h3>
              <div className={classes.mRole}>{TEAM_MEMORIAL.role}</div>
              <p>{TEAM_MEMORIAL.bio}</p>
            </div>
          </div>
        </div>
      </section>

      {/* COMPANY INFO */}
      <section className={`${classes.block} ${classes.alt}`}>
        <div className={classes.wrap}>
          <span className={classes.kicker}>Company information</span>
          <h2>Awasource Ltd</h2>
          <div className={classes.info}>
            {COMPANY_INFO.map(({ k, v }) => (
              <div key={k} className={classes.item}>
                <div className={classes.k}>{k}</div>
                <div className={classes.v}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className={classes.footerWrap}>
        <Footer />
      </div>
      </div>
    </>
  );
};

export default Team;
